import { Email, HdrAuto, MailOutline, Sms } from '@mui/icons-material';
import { Stack, Chip, Tooltip, Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Colors } from 'src/shared/components/styles/colors';
import { formatDate } from 'src/shared/functions/formatDate';
import { InvoiceDeliveryMethodStringEnum } from 'src/shared/models/enums/invoiceDeliveryMethodString.enum';
import { InvoiceSending } from '../models/invoiceSending';

const getLatestSending = (sendings: InvoiceSending[]): InvoiceSending | undefined => {
  if (!sendings || sendings.length === 0) return undefined;

  return sendings.reduce((latest, current) => {
    if (!latest.deliveryDate || dayjs(current.deliveryDate).isAfter(dayjs(latest.deliveryDate))) {
      return current;
    }
    return latest;
  }, sendings[0]);
};

const getIcon = (deliveryMethod: string) => {
  switch (deliveryMethod) {
    case InvoiceDeliveryMethodStringEnum.Email:
      return <Email sx={{ fill: Colors.ColorBox.Blue }} />;
    case InvoiceDeliveryMethodStringEnum.SMS:
      return <Sms sx={{ fill: Colors.ColorBox.Blue }} />;
    case InvoiceDeliveryMethodStringEnum.AutoGiro:
      return <HdrAuto sx={{ fill: Colors.ColorBox.Blue }} />;
    case InvoiceDeliveryMethodStringEnum.Card:
    case InvoiceDeliveryMethodStringEnum.Print:
    case InvoiceDeliveryMethodStringEnum.Letter:
    case InvoiceDeliveryMethodStringEnum.Unknown:
    case InvoiceDeliveryMethodStringEnum.File:
      return <MailOutline sx={{ fill: Colors.ColorBox.Blue }} />;
    default:
      return <MailOutline sx={{ fill: Colors.ColorBox.Blue }} />;
  }
};

type InvoiceDeliveryChipProps = {
  sendings: InvoiceSending[];
  chipSize: 'small' | 'medium';
};

export const InvoiceDeliveryChip = (props: InvoiceDeliveryChipProps) => {
  const { sendings, chipSize } = props;
  const { t } = useTranslation(['invoice']);
  if (!sendings) return <></>;

  const latestSending = getLatestSending(sendings);
  if (!latestSending) return <></>;

  const getTooltipText = (items: InvoiceSending[]) => {
    if (!items || items.length === 0) return [];
    const sendingsText = items.map((sending) => {
      return `${formatDate(sending.deliveryDate)} ${t(`invoice:deliveryMethod.${sending.deliveryMethod?.trim()}`)} (${t(
        `invoice:deliveryType.${sending.type?.trim()}`,
      )})`;
    });
    return sendingsText;
  };

  return (
    <Stack direction='row' spacing={1} sx={{ '& .MuiChip-filled ': { borderRadius: 1 } }}>
      <Tooltip
        arrow
        title={
          <Box>
            <Typography fontSize={12} fontWeight='bold'>
              {t('invoice:history')}
            </Typography>
            {getTooltipText(sendings).map((item: string, index: number) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={index} fontSize={12}>
                  {item}
                </Typography>
              );
            })}
          </Box>
        }
      >
        <Chip
          label={t(`invoice:deliveryMethod.${latestSending.deliveryMethod?.trim()}`)}
          icon={getIcon(latestSending?.deliveryMethod ?? '')}
          sx={{ backgroundColor: Colors.ChipColors.DustyBlue, borderRadius: 1, ml: 2, color: Colors.ColorBox.Blue }}
          size={chipSize}
        />
      </Tooltip>
    </Stack>
  );
};
